import { EditOutlined, MoreOutlined, UserAddOutlined } from '@ant-design/icons';
import PermissionValidator from '@authorization/PermissionValidator';
import { AccountRoles } from '@authorization/Roles';
import { EssityColumnProps } from '@components/shared/paginatedEssityTable/GridHelper';
import PaginatedEssityTable from '@components/shared/paginatedEssityTable/PaginatedEssityTable';
import { SieveModel } from '@components/shared/paginatedEssityTable/SieveModels';
import { TableWithTitle } from '@components/shared/TableWithTitle';
import { ajaxByUser } from '@helper/api';
import { useRootData } from '@hooks/hook';
import { getEssityApiClient } from '@services/EssityApi';
import {
	PharmacyAllOptionalParams,
	PharmacyDto,
	PharmacyUserType,
	PortalStatus,
} from '@services/src/models';
import { Button, Dropdown, Menu, Tooltip } from 'antd';
import React from 'react';
import { useHistory } from 'react-router';
import { IAccountDetailsStore } from 'stores/AccountDetailsStore';
import { IGridStore } from 'stores/GridStore';

import { GetPharmaciesTableColumns } from './PharmaciesTableColumns';
import styles from './styles/Pharmacies.module.less';

export const PharmaciesTable = () => {
	const history = useHistory();

	const accountDetailsStore: IAccountDetailsStore = useRootData(
		(store) => store.accountDetailsStore
	);

	const gridStore: IGridStore = useRootData((store) => store.gridStore);

	const sendInvitation = (
		pharmacyId: string,
		pharmacyUserType: PharmacyUserType
	) => {
		ajaxByUser(
			'Successfully sent invitation.',
			() =>
				getEssityApiClient().then((api) =>
					api.account
						.invitePharmacy({
							body: {
								pharmacyId,
								pharmacyUserType,
							},
						})
						.then(() => {
							gridStore.searching.set(true);
						})
				),
			() => {}
		);
	};

	const isBusinessAdmin = (account: any) =>
		new PermissionValidator(account).hasRole(AccountRoles.BUSINESS_ADMIN);

	const columns: EssityColumnProps<PharmacyDto>[] = [
		...GetPharmaciesTableColumns({ allowFilterOnVoivodeship: true }),
		{
			title: '',
			key: 'operation',
			fixed: 'right',
			width: 50,
			align: 'center',
			render: (item: PharmacyDto) => {
				const account = accountDetailsStore.account.get();
				const isAdmin = account && isBusinessAdmin(account);

				const mainUser = item.pharmacyUsers?.find(
					(u) => u.type === PharmacyUserType.Main
				);
				const secondaryUser = item.pharmacyUsers?.find(
					(u) => u.type === PharmacyUserType.Secondary
				);

				const menu = (
					<Menu>
						<Menu.Item
							key="edit"
							icon={<EditOutlined />}
							onClick={() =>
								history.push(`pharmacies/edit/${item.id}`)
							}
						>
							Edit
						</Menu.Item>
						{isAdmin && (
							<Menu.Item
								key="invite-main"
								icon={<UserAddOutlined />}
								disabled={
									mainUser?.portalStatus ===
										PortalStatus.Access ||
									mainUser?.portalStatus === PortalStatus.Sent
								}
								onClick={() => {
									if (
										mainUser?.portalStatus !==
											PortalStatus.Access &&
										mainUser?.portalStatus !==
											PortalStatus.Sent
									) {
										sendInvitation(
											item.id!,
											PharmacyUserType.Main
										);
									}
								}}
							>
								Send portal invitation - main
							</Menu.Item>
						)}
						{isAdmin && (
							<Menu.Item
								key="invite-secondary"
								icon={<UserAddOutlined />}
								disabled={
									secondaryUser?.portalStatus ===
										PortalStatus.Access ||
									secondaryUser?.portalStatus ===
										PortalStatus.Sent
								}
								onClick={() => {
									if (
										secondaryUser?.portalStatus !==
											PortalStatus.Access &&
										secondaryUser?.portalStatus !==
											PortalStatus.Sent
									) {
										sendInvitation(
											item.id!,
											PharmacyUserType.Secondary
										);
									}
								}}
							>
								Send portal invitation - pharmacy
							</Menu.Item>
						)}
					</Menu>
				);

				return (
					<Dropdown overlay={menu} trigger={['click']}>
						<Button
							type="primary"
							shape="circle"
							size="small"
							icon={<MoreOutlined />}
						/>
					</Dropdown>
				);
			},
		},
	];

	return (
		<TableWithTitle title="Pharmacies">
			<PaginatedEssityTable<PharmacyDto>
				className={styles.table}
				columns={columns}
				gridName="pharmacies"
				hasRowSelection={true}
				hidePersonalizationSettings={true}
				getRowKey={(r: PharmacyDto) => r.id!}
				detailsUrl="pharmacies/details"
				getPagedResult={async (sieve: SieveModel, abortSignal) => {
					const parameters: PharmacyAllOptionalParams = {
						page: sieve.page,
						pageSize: sieve.pageSize,
						sorts: sieve.sorts,
						abortSignal: abortSignal,
						filters: sieve.filters,
					};

					const api = await getEssityApiClient();
					return await api.pharmacy.all(parameters);
				}}
			/>
		</TableWithTitle>
	);
};
